// Formatting shared by the cards in the approvals inbox. A group card and the
// request cards inside it sit next to each other, so an amount or an age that
// read differently between them would look like a discrepancy in the data.

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// A date-only string is a calendar date and must not be pulled into the local
// timezone by the Date constructor, which is what would turn Aug 01 into Jul 31
// for anyone west of UTC.
const toLocalDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = DATE_ONLY_PATTERN.exec(value.trim());
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => {
  const date = toLocalDate(value);
  if (!date) return value ? String(value) : '';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

// Year dropped: a row in a batch is read against the rows beside it, which are
// days apart, and the column has to stay narrow.
export const formatShortDate = (value) => {
  const date = toLocalDate(value);
  if (!date) return value ? String(value) : '';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
};

export const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

// "3 days ago" reads as pressure in a queue in a way a date does not.
export const formatAge = (value) => {
  if (!value) return '';
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '';
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
};

// Expense and time off rows name the employee differently depending on which
// join produced them, so every caller goes through this.
export const employeeName = (record) => {
  const first = record?.first_name || record?.employee_first_name || '';
  const last = record?.last_name || record?.employee_last_name || '';
  return `${first} ${last}`.trim() || record?.email || 'Unknown Employee';
};
